import { EventrDate, formatDate } from '@eventr/common'
import {
    ColoursObj,
    Icon,
    MainButton,
    Typography,
} from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useMemo, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { DisclosureGroup } from '../../common/DisclosureGroup'
import { GuestBanner } from '../../feat/guest/components/guestBanner'
import i18n from '../../i18n/i18next'
import { bs, useBoundStore } from '../../state/bound.store'
import { useLanguageContext } from '../../state/languageState/languageContext'
import { useRouteGuardContext } from '../../state/routeGuardProvider'
import { userPrefShowDestTime } from '../../state/userPrefsState/userPrefsSignels'
import { useTotalPendingTodoCount } from '../../v2/componentCards/hooks/usePendingTodoCount'
import { TodoDisclosureGroup } from '../../v2/componentCards/todoComponents/todoDisclosureGroup'
import { ScreenTypeProvider } from '../../v2/state/screenTypeProvider'
import Next from '../Next'
import SkeletoScreen from '../components/SkeletonScreen'
import AddEmptyItinerarySplash from './AddEmptyItinerarySplash'

export const Itinerary = () => {
    const {
        noItineraries,
        eventEnded,
        past,
        getEventInfoV2,
        itineraryComponents,
        selectedItinEmpty,
    } = useBoundStore(
        useShallow((s) => {
            return {
                noItineraries: s.noItineraries,
                eventEnded: s.eventEnded,
                past: s.past,
                getEventInfoV2: s.getEventInfoV2,
                itineraryComponents: s.itineraryComponents,
                selectedItinEmpty: s.selectedItinEmpty,
            }
        })
    )

    const totalPending = useTotalPendingTodoCount()

    const containerRef = useRef(null)
    const { isAllowed } = useRouteGuardContext()
    const { getLanguageISO639Code } = useLanguageContext()

    const components = useMemo(() => {
        return past ? itineraryComponents?.past : itineraryComponents.present
    }, [itineraryComponents, past])

    const content = useMemo(() => {
        return (
            <ScreenTypeProvider screenType="upNext">
                {!past && <TodoDisclosureGroup />}
                {Object.keys(components)
                    .filter((key) => {
                        return key !== 'Other'
                    })
                    .map((key, index) => {
                        return (
                            <DisclosureGroup
                                stickyHeader
                                childrenHandlePadding
                                id={index.toString()}
                                key={index}
                                containerStyle={{
                                    backgroundColor:
                                        ColoursObj.backgrounds.primary,
                                }}
                                title={
                                    key !== 'Other'
                                        ? formatDate(
                                              new EventrDate(
                                                  components[key][0].date
                                              ).setTimezoneEnum(
                                                  new EventrDate()
                                                      .timezoneNumber ?? 0,
                                                  userPrefShowDestTime.value ===
                                                      'itinLocal'
                                                      ? true
                                                      : false
                                              ).dateTimeString,
                                              userPrefShowDestTime.value,
                                              getLanguageISO639Code()
                                          )
                                        : key
                                }
                                isOpen={index < 2}
                                count={components[key].length}
                                icon={
                                    <Icon
                                        name="Calendar"
                                        iconColour={
                                            ColoursObj.backgrounds.primary
                                        }
                                        bubble
                                    />
                                }
                                content={<Next components={components[key]} />}
                            />
                        )
                    })}
                {components['Other'] &&
                    components['Other'].length !== 0 &&
                    !selectedItinEmpty && (
                        <DisclosureGroup
                            stickyHeader
                            title={'Other'}
                            isOpen={true}
                            containerStyle={{
                                backgroundColor: ColoursObj.backgrounds.primary,
                            }}
                            count={components['Other'].length}
                            icon={
                                <Icon
                                    name="Calendar"
                                    iconColour={ColoursObj.backgrounds.primary}
                                    bubble
                                />
                            }
                            content={<Next components={components['Other']} />}
                        />
                    )}
            </ScreenTypeProvider>
        )
    }, [
        components,
        userPrefShowDestTime.value,
        getLanguageISO639Code,
        selectedItinEmpty,
        past,
    ])
    const isItinEmpty = useMemo(() => {
        return selectedItinEmpty && totalPending < 1 && !past
    }, [selectedItinEmpty, totalPending, past])

    return (
        <div ref={containerRef} style={{ margin: '0px 0px 128px 0px' }}>
            {getEventInfoV2() === undefined ||
            noItineraries === undefined ||
            eventEnded === undefined ||
            isAllowed(['allowedEdit']) === undefined ||
            selectedItinEmpty === undefined ? (
                <SkeletoScreen />
            ) : (
                <Stack justifyContent={'space-between'} height={'100%'}>
                    <>
                        {isItinEmpty && (
                            <>
                                <GuestBanner />
                                <AddEmptyItinerarySplash />
                            </>
                        )}
                        {past && (
                            <Stack paddingBottom={'16px'}>
                                <Typography
                                    variant="bodySubtext"
                                    style={{
                                        width: '100%',
                                        padding: '16px 0px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                    color={ColoursObj.core.primary}
                                >
                                    {i18n.t('home.viewingPast')}
                                </Typography>
                                <MainButton
                                    id="returnPresent"
                                    style={{
                                        margin: '0px 16px',
                                    }}
                                    onClick={() => {
                                        bs().setPast(false)
                                    }}
                                >
                                    {i18n.t('home.returnPresent')}
                                </MainButton>
                            </Stack>
                        )}
                        {!isItinEmpty && (
                            <>
                                <GuestBanner />
                                {content}
                            </>
                        )}
                    </>
                </Stack>
            )}
        </div>
    )
}
